import React, { forwardRef, useImperativeHandle, useState } from "react";
import { ICellEditor } from "@ag-grid-community/core";
import { Content } from "@iventis/translations/content/typed-content";
import { IventisCellEditorParams, RowNetworkStatus } from "../types/data-table.types";
import { StyledAutocomplete } from "./list-item-cell-editor";

const noSelectionOption = { id: undefined, name: undefined, boolean: undefined };

/**
 * Used as cellEditor for data table column of type "boolean"
 */
export const BooleanCellEditor = forwardRef<ICellEditor, IventisCellEditorParams>(({ value: inputValue, node, required, column, context }, ref) => {
    const translate = context.translate ? context.translate : (t) => t;

    const options = [{ id: "true", name: translate(Content.common.yes), boolean: true }, { id: "false", name: translate(Content.common.no), boolean: false }, noSelectionOption];
    const initialValue = options.find((item) => item.boolean === inputValue);

    const [value, setValue] = useState<{ id: string; name: string; boolean: boolean }>(initialValue);

    useImperativeHandle(ref, () => ({
        getValue() {
            return value.boolean;
        },
        isCancelBeforeStart() {
            // If the field is not required or has a value, while we are pending creation, make this cell uneditable
            const blockEditsUntilCreatedOnServer = !required && node.data.networkStatus === RowNetworkStatus.PendingCreation;
            return !node.data.canEdit || blockEditsUntilCreatedOnServer;
        },
        isCancelAfterEnd() {
            return value.boolean === inputValue;
        },
    }));

    return (
        <StyledAutocomplete
            loadingOptions={false}
            disableClearable
            style={{ width: column.getActualWidth() }}
            value={value || noSelectionOption}
            disablePortal
            autoFocus
            variant="standard"
            onChange={(value: { id: string; name: string } | string) => {
                const listItemId = typeof value === "string" ? value : value.id;
                setValue(options.find((i) => i?.id === listItemId || i?.name === listItemId));
            }}
            options={options}
            filterOptions={(options) => options.filter((option) => option !== noSelectionOption)}
            getOptionLabel={(v) => (v as { name: string })?.name || ""}
            error={false}
        />
    );
});
